import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatMessage, ChatRequest, AgentUser } from '../../types';

interface Chat {
  id: string;
  subject: string;
  participants: string[];
  messages: ChatMessage[];
  isActive: boolean;
  lastActivity: Date;
}

interface ChatState {
  activeChat: string | null;
  activeChats: { [chatId: string]: Chat };
  messages: { [chatId: string]: ChatMessage[] };
  pendingRequests: ChatRequest[];
  typingUsers: { [chatId: string]: string[] };
  unreadCount: number;
  loading: boolean;
  error: string | null;
  users?: AgentUser[];
}

const initialState: ChatState = {
  activeChat: null,
  activeChats: {},
  messages: {},
  pendingRequests: [],
  typingUsers: {},
  unreadCount: 0,
  loading: false,
  error: null,
  users: []
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addChat: (state, action: PayloadAction<Chat>) => {
      state.activeChats[action.payload.id] = action.payload;
    },
    removeChat: (state, action: PayloadAction<string>) => {
      delete state.activeChats[action.payload];
    },
    addMessage: (state, action: PayloadAction<{ chatId: string; message: ChatMessage }>) => {
      const MAX_MESSAGES_PER_CHAT = 500; // Limit messages to prevent memory leaks
      
      const chat = state.activeChats[action.payload.chatId];
      if (chat) {
        chat.messages.push(action.payload.message);
        // Keep only the last MAX_MESSAGES_PER_CHAT messages
        if (chat.messages.length > MAX_MESSAGES_PER_CHAT) {
          chat.messages = chat.messages.slice(-MAX_MESSAGES_PER_CHAT);
        }
        chat.lastActivity = new Date();
      }
      // Increment unread count if message is from another user and chat is not active
      if (action.payload.message.senderId !== 'current-user' && action.payload.chatId !== state.activeChat) {
        state.unreadCount++;
      }
      // Also add to the messages state
      if (!state.messages[action.payload.chatId]) {
        state.messages[action.payload.chatId] = [];
      }
      state.messages[action.payload.chatId].push(action.payload.message);
      // Keep only the last MAX_MESSAGES_PER_CHAT messages
      if (state.messages[action.payload.chatId].length > MAX_MESSAGES_PER_CHAT) {
        state.messages[action.payload.chatId] = state.messages[action.payload.chatId].slice(-MAX_MESSAGES_PER_CHAT);
      }
    },
    setActiveChat: (state, action: PayloadAction<string | null>) => {
      state.activeChat = action.payload;
      // Reset unread count when opening a chat
      if (action.payload) {
        state.unreadCount = 0;
      }
    },
    incrementUnreadCount: (state) => {
      state.unreadCount++;
    },
    resetUnreadCount: (state) => {
      state.unreadCount = 0;
    },
    updateChatStatus: (state, action: PayloadAction<{ chatId: string; status: string }>) => {
      const chat = state.activeChats[action.payload.chatId];
      if (chat) {
        chat.isActive = action.payload.status !== 'ended';
      }
    },
    updateMessageStatus: (state, action: PayloadAction<{ 
      chatId: string; 
      messageId: string; 
      status: ChatMessage['status'] 
    }>) => {
      const chat = state.activeChats[action.payload.chatId];
      if (chat) {
        const message = chat.messages.find(m => m.id === action.payload.messageId);
        if (message) {
          message.status = action.payload.status;
        }
      }
    },
    addChatRequest: (state, action: PayloadAction<ChatRequest>) => {
      const MAX_PENDING_REQUESTS = 50; // Limit pending requests to prevent memory leaks
      
      state.pendingRequests.push(action.payload);
      // Keep only the last MAX_PENDING_REQUESTS
      if (state.pendingRequests.length > MAX_PENDING_REQUESTS) {
        state.pendingRequests = state.pendingRequests.slice(-MAX_PENDING_REQUESTS);
      }
    },
    removeChatRequest: (state, action: PayloadAction<number>) => {
      state.pendingRequests.splice(action.payload, 1);
    },
    setTypingUsers: (state, action: PayloadAction<{ chatId: string; users: string[] }>) => {
      state.typingUsers[action.payload.chatId] = action.payload.users;
    },
    setChatLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setChatError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  }
});

export const {
  addChat,
  removeChat,
  addMessage,
  setActiveChat,
  updateChatStatus,
  updateMessageStatus,
  addChatRequest,
  removeChatRequest,
  setTypingUsers,
  incrementUnreadCount,
  resetUnreadCount,
  setChatLoading,
  setChatError
} = chatSlice.actions;

export default chatSlice.reducer;